import { useMemo } from "react";
import { useMutation, useMutationState } from "@tanstack/react-query";

import { submitEstateItem } from "../shared/probateEstate/submitEstateItem";

// The staff app adds a client's item and resends the case later, so until then the ledger doesn't
// hold it. We show what was sent until the case comes back with different ledger items, and from
// then on the ledger is the record. A resend for another reason, such as a new item from an upload,
// hides it early; it comes back with the next resend.
const ESTATE_ITEM = "estateItem";

const ledgerItemIds = (ledger) =>
  (ledger?.items ?? [])
    .map((item) => item.id)
    .sort()
    .join(",");

export const useSendEstateItem = (kase) => {
  const { mutateAsync } = useMutation({
    mutationKey: [ESTATE_ITEM, kase?.id],
    mutationFn: ({ submission }) => submitEstateItem(kase.id, submission),
    // The cache is the only record of what was sent, so it lasts until log out clears it
    gcTime: Infinity,
  });
  return (submission) => mutateAsync({ caseId: kase.id, ledgerItemIds: ledgerItemIds(kase.ledger), submission });
};

export const useSentEstateItems = (kase) => {
  // select only runs when the mutation cache changes, so the ledger is compared outside it
  const sent = useMutationState({
    filters: { mutationKey: [ESTATE_ITEM], status: "success" },
    select: (mutation) => ({ id: `sent-${mutation.mutationId}`, ...mutation.state.variables }),
  });
  return useMemo(() => {
    const current = ledgerItemIds(kase.ledger);
    return sent
      .filter(({ caseId, ledgerItemIds: idsWhenSent }) => caseId === kase.id && idsWhenSent === current)
      .map(({ id, submission: { payload } }) => ({
        id,
        item_type: payload.item_type,
        description: payload.description,
        institution_name: payload.institution?.name ?? null,
        reference: payload.account_reference ?? null,
      }));
  }, [sent, kase]);
};
